import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  InlineSelect,
  InlineSelectMenu,
  InlineSelectOption,
  InlineSelectTrigger,
} from 'controlled-components'
import {
  DEFAULT_GROUP_ID,
  selectGroupLevel1OperatorOptions,
  selectGroupTopOperator,
  setGroupTopOperator,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Constants (fallback when no server config)
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_OPERATOR_OPTIONS = [
  { id: 'any', text: 'any' },
  { id: 'all', text: 'all' },
  { id: 'none', text: 'none' },
]

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface TopOperatorContainerProps {
  groupId?: string
  disabled?: boolean
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

const TopOperatorContainer = React.memo(
  ({
    groupId = DEFAULT_GROUP_ID,
    disabled = false,
  }: TopOperatorContainerProps) => {
    const dispatch = useDispatch()

    // Local UI state for dropdown open/close
    const [isOpen, setIsOpen] = useState(false)

    // Read from Redux - per-group top operator
    const operator = useSelector((state: any) =>
      selectGroupTopOperator(state, groupId)
    )

    // Get operator options from group config or use defaults
    const serverOperatorOptions = useSelector((state: any) =>
      selectGroupLevel1OperatorOptions(state, groupId)
    )
    const operatorOptions =
      serverOperatorOptions.length > 0
        ? serverOperatorOptions
        : DEFAULT_OPERATOR_OPTIONS

    // Get selected option label for display
    const selectedOption = operatorOptions.find(opt => opt.id === operator)
    const selectedLabel = selectedOption?.text ?? 'any'

    // Memoized handlers
    const handleToggle = useCallback(() => {
      if (disabled) return
      setIsOpen(prev => !prev)
    }, [disabled])

    const handleClose = useCallback(() => {
      setIsOpen(false)
    }, [])

    const handleSelect = useCallback(
      (value: string) => {
        dispatch(setGroupTopOperator({ groupId, operator: value }))
        setIsOpen(false)
      },
      [dispatch, groupId]
    )

    return (
      <InlineSelect isOpen={isOpen} onClose={handleClose}>
        <InlineSelectTrigger
          isOpen={isOpen}
          onToggle={handleToggle}
          disabled={disabled}
        >
          {selectedLabel}
        </InlineSelectTrigger>
        {!disabled && (
          <InlineSelectMenu isOpen={isOpen}>
            {operatorOptions.map(opt => (
              <InlineSelectOption
                key={opt.id}
                value={opt.id}
                selected={operator === opt.id}
                onSelect={handleSelect}
              >
                {opt.text}
              </InlineSelectOption>
            ))}
          </InlineSelectMenu>
        )}
      </InlineSelect>
    )
  }
)

export default TopOperatorContainer
